import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import usePagination from '../../../hooks/usePagination';
import Button from '../../buttons/Button';
import AsLinkButton from '../../buttons/AsLinkButton';

/**
 * A component for organising pages of results
 */
function Pagination({ total, current, onPageChange }) {
  const { t } = useTranslation();

  const pagination = usePagination(total, current);

  const isFirstPage = current === 1;
  const isLastPage = current === total;

  if (!total || total <= 1) {
    return null;
  }

  return (
    <nav aria-label="pagination" className="pagination">
      <span>{t('styleguide:pagination')}</span>
      <ul>
        <li
          className={classNames('pagination__previous', {
            ['pagination--on-first']: isFirstPage
          })}
        >
          <Button
            key={`pagination-previous-${current}`} // Use key to rerender and shift focus to top of page.
            title={`${t('styleguide:link_to')} ${t('styleguide:previous_page')}`}
            onPress={() => onPageChange(current - 1)}
          >
            <span aria-hidden="true">&lt;</span>&nbsp;{t('styleguide:back')}
            <span className={'visually-hidden'}>&nbsp;{t('styleguide:to_previous')}</span>
          </Button>
          <div className="pagination__placeholder">
            <span aria-hidden="true">&lt;</span>&nbsp;{t('styleguide:back')}
            <span className={'visually-hidden'}>&nbsp;{t('styleguide:to_previous')}</span>
          </div>
        </li>
        {pagination.map((page, index) => {
          const hasEllipses = pagination[index - 1] === '...';
          const isCurrentPage = page === current;
          const isLastPage = page === total;
          const itemClasses = classNames('pagination__item', {
            ['item--current']: isCurrentPage,
            ['item--last']: isLastPage
          });

          // Check if the page is a number not ellipses.
          if (typeof page === 'number') {
            return (
              <li key={index} className={itemClasses}>
                {hasEllipses && <div className="item__ellipsis">...</div>}
                {isCurrentPage ? (
                  page
                ) : (
                  <AsLinkButton
                    title={`${t('styleguide:page')} ${page}`}
                    onPress={() => onPageChange(page)}
                    ariaLabel={`${t('styleguide:page')} ${page}`}
                  >
                    <span>{t('styleguide:page')}&nbsp;</span>
                    {page}
                  </AsLinkButton>
                )}
              </li>
            );
          }
        })}
        <li
          className={classNames('pagination__next', {
            ['pagination--on-last']: isLastPage
          })}
        >
          <Button
            key={`pagination-next-${current}`} // Use key to rerender and shift focus to top of page.
            title={`${t('styleguide:link_to')} ${t('styleguide:next_page')}`}
            onPress={() => onPageChange(current + 1)}
          >
            {t('styleguide:next')}&nbsp;<span aria-hidden="true">&gt;</span>
            <span className={'visually-hidden'}>&nbsp;{t('styleguide:page')}</span>
          </Button>
          <div className="pagination__placeholder">
            {t('styleguide:next')}&nbsp;<span aria-hidden="true">&gt;</span>
            <span className={'visually-hidden'}>&nbsp;{t('styleguide:page')}</span>
          </div>
        </li>
        <li className="pagination__mobile-pages">
          {t('styleguide:page')}&nbsp;{current}&nbsp;{t('styleguide:of')}&nbsp;{total}
        </li>
      </ul>
    </nav>
  );
}

Pagination.propTypes = {
  /**
   * The total number of pages
   */
  total: PropTypes.number.isRequired,
  /**
   * The current page
   */
  current: PropTypes.number.isRequired,
  /**
   * The page change handler
   */
  onPageChange: PropTypes.func
};

export default Pagination;
